/**
 * features/documents/DocumentsTab.tsx
 * ------------------------------------------------------------------
 * Shared documents UI used by:
 *   - StudentProfile  (admissions staff view)
 *   - AdmissionsModule edit form (admissions staff view)
 *   - ParentPortal    (linked-parent read-only view)
 *
 * Behaviour:
 *   - Lists existing documents (newest first)
 *   - readOnly=true mode hides Upload + Delete and shows only Download
 *   - Upload form: type dropdown, custom label (when type=other),
 *     notes textarea, file picker. Client-side validation enforces
 *     PDF/JPG/PNG/WebP + 10 MB cap; the server enforces the same.
 *   - Download fetches a fresh signed URL each time (URLs are
 *     time-limited).
 *   - Delete asks for confirmation, then removes the metadata
 *     and the storage file in one go.
 *
 * Errors from the underlying SDK (permission denied, network etc.)
 * surface inline via the `msg` state.
 * ------------------------------------------------------------------
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle2,
  File as FileIcon,
  Image as ImageIcon,
  X,
} from 'lucide-react';
import {
  listStudentDocuments,
  uploadStudentDocument,
  getDocumentDownloadUrl,
  deleteStudentDocument,
  validateDocumentFile,
  formatFileSize,
  displayLabel,
  MAX_FILE_SIZE_BYTES,
} from '@/lib/studentDocuments';
import { SoftCard } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import type { StudentDocument, StudentDocumentType } from '@/types';
import { STUDENT_DOCUMENT_TYPE_LABELS } from '@/types';

interface Props {
  studentId: string;
  /** Set to true for the parent portal — hides Upload + Delete. */
  readOnly?: boolean;
  /** Optional: who's uploading (admin / admissions / headteacher). */
  uploader?: { uid: string; displayName?: string };
}

const TYPE_OPTIONS: { id: StudentDocumentType; label: string }[] = [
  { id: 'birth_certificate', label: STUDENT_DOCUMENT_TYPE_LABELS.birth_certificate },
  { id: 'passport', label: STUDENT_DOCUMENT_TYPE_LABELS.passport },
  { id: 'residence_permit', label: STUDENT_DOCUMENT_TYPE_LABELS.residence_permit },
  { id: 'parent_id', label: STUDENT_DOCUMENT_TYPE_LABELS.parent_id },
  { id: 'previous_transcript', label: STUDENT_DOCUMENT_TYPE_LABELS.previous_transcript },
  { id: 'vaccination_record', label: STUDENT_DOCUMENT_TYPE_LABELS.vaccination_record },
  { id: 'other', label: STUDENT_DOCUMENT_TYPE_LABELS.other },
];

export default function DocumentsTab({ studentId, readOnly = false, uploader }: Props) {
  const [docs, setDocs] = useState<StudentDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Upload form
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingType, setPendingType] = useState<StudentDocumentType>('birth_certificate');
  const [pendingCustomLabel, setPendingCustomLabel] = useState('');
  const [pendingNotes, setPendingNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track which docs are mid-action (downloading / deleting) for spinners
  const [busyDocId, setBusyDocId] = useState<string | null>(null);

  /* ---- load ---- */
  async function refresh() {
    try {
      const list = await listStudentDocuments(studentId);
      setDocs(list);
    } catch (e) {
      setMsg({ ok: false, text: `Failed to load documents: ${(e as Error).message}` });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  /* ---- handlers ---- */

  function handleFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateDocumentFile(file);
    if (err) {
      setMsg({ ok: false, text: err });
      e.target.value = '';
      return;
    }
    setPendingFile(file);
    setMsg(null);
  }

  function resetUploadForm() {
    setShowUploadForm(false);
    setPendingFile(null);
    setPendingType('birth_certificate');
    setPendingCustomLabel('');
    setPendingNotes('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleUpload() {
    if (!pendingFile) {
      setMsg({ ok: false, text: 'Pick a file first.' });
      return;
    }
    if (pendingType === 'other' && !pendingCustomLabel.trim()) {
      setMsg({ ok: false, text: 'Enter a label for the document.' });
      return;
    }
    if (!uploader) {
      setMsg({ ok: false, text: 'Cannot determine uploader. Please sign in again.' });
      return;
    }

    setUploading(true);
    setMsg(null);
    try {
      await uploadStudentDocument({
        studentId,
        file: pendingFile,
        type: pendingType,
        customLabel: pendingType === 'other' ? pendingCustomLabel.trim() : undefined,
        notes: pendingNotes.trim() || undefined,
        uploadedBy: uploader.uid,
        uploadedByName: uploader.displayName,
      });
      setMsg({ ok: true, text: 'Document uploaded.' });
      resetUploadForm();
      await refresh();
      setTimeout(() => setMsg(null), 2500);
    } catch (e) {
      setMsg({ ok: false, text: `Upload failed: ${(e as Error).message}` });
    } finally {
      setUploading(false);
    }
  }

  async function handleDownload(record: StudentDocument) {
    setBusyDocId(record.id);
    setMsg(null);
    try {
      const url = await getDocumentDownloadUrl(record);
      // Open in a new tab — browser handles inline view for PDFs/images
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setMsg({ ok: false, text: `Download failed: ${(e as Error).message}` });
    } finally {
      setBusyDocId(null);
    }
  }

  async function handleDelete(record: StudentDocument) {
    if (
      !confirm(
        `Delete "${displayLabel(record)}" (${record.filename})? This cannot be undone.`,
      )
    ) {
      return;
    }
    setBusyDocId(record.id);
    setMsg(null);
    try {
      await deleteStudentDocument(record);
      setMsg({ ok: true, text: 'Document deleted.' });
      await refresh();
      setTimeout(() => setMsg(null), 2500);
    } catch (e) {
      setMsg({ ok: false, text: `Delete failed: ${(e as Error).message}` });
    } finally {
      setBusyDocId(null);
    }
  }

  /* ---- render ---- */

  const presentTypes = useMemo(() => new Set(docs.map((d) => d.type)), [docs]);

  if (loading) return <Spinner label="Loading documents…" />;

  return (
    <div className="space-y-4">
      {msg && (
        <div
          className={`p-3 rounded-lg border text-sm flex items-start gap-2 ${
            msg.ok
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
              : 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/30 dark:text-red-300'
          }`}
        >
          {msg.ok ? <CheckCircle2 size={16} className="mt-0.5" /> : <AlertCircle size={16} className="mt-0.5" />}
          <span>{msg.text}</span>
        </div>
      )}

      {/* ---- Upload affordance (admissions staff only) ---- */}
      {!readOnly && !showUploadForm && (
        <button
          onClick={() => setShowUploadForm(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-[#2d5295] hover:text-[#2d5295] transition-colors"
        >
          <Upload size={16} />
          Upload a document
        </button>
      )}

      {!readOnly && showUploadForm && (
        <SoftCard>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <Upload size={16} className="text-[#2d5295]" /> Upload document
            </h3>
            <button onClick={resetUploadForm} className="text-gray-400 hover:text-gray-700">
              <X size={16} />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document type <span className="text-red-500">*</span>
              </label>
              <select
                value={pendingType}
                onChange={(e) => setPendingType(e.target.value as StudentDocumentType)}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:border-[#2d5295] text-sm"
              >
                {TYPE_OPTIONS.map((t) => {
                  const exists = presentTypes.has(t.id) && t.id !== 'other';
                  return (
                    <option key={t.id} value={t.id}>
                      {t.label}{exists ? ' (already uploaded)' : ''}
                    </option>
                  );
                })}
              </select>
              {presentTypes.has(pendingType) && pendingType !== 'other' && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <AlertCircle size={11} />
                  A "{STUDENT_DOCUMENT_TYPE_LABELS[pendingType]}" already exists. Uploading will add a second copy.
                </p>
              )}
            </div>

            {pendingType === 'other' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Custom label <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={pendingCustomLabel}
                  onChange={(e) => setPendingCustomLabel(e.target.value)}
                  maxLength={80}
                  placeholder="e.g. Medical letter, Sports waiver…"
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:border-[#2d5295] text-sm"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                File <span className="text-red-500">*</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={handleFilePick}
                className="w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-[#2d5295] file:text-white file:text-xs file:font-semibold hover:file:bg-[#1b3a6b] file:cursor-pointer"
              />
              <p className="mt-1 text-xs text-gray-400">
                PDF, JPG, PNG, or WebP. Max {(MAX_FILE_SIZE_BYTES / 1024 / 1024).toFixed(0)} MB.
              </p>
              {pendingFile && (
                <div className="mt-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 text-xs flex items-center gap-2">
                  <FileIcon size={12} className="text-gray-400" />
                  <span className="flex-1 truncate text-gray-700 dark:text-gray-200">
                    {pendingFile.name}
                  </span>
                  <span className="text-gray-400">{formatFileSize(pendingFile.size)}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={pendingNotes}
                onChange={(e) => setPendingNotes(e.target.value)}
                rows={2}
                maxLength={500}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:border-[#2d5295] text-sm"
                placeholder="e.g. Original received from parent; copy returned."
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={resetUploadForm}
                disabled={uploading}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !pendingFile}
                className="px-4 py-2 rounded-lg bg-[#2d5295] text-white text-sm font-semibold hover:bg-[#1b3a6b] disabled:opacity-50 flex items-center gap-2"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </SoftCard>
      )}

      {/* ---- Document list ---- */}
      {docs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText size={32} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm">
            {readOnly
              ? 'No documents have been uploaded for this student yet.'
              : 'No documents yet. Click "Upload a document" to add one.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => {
            const isImage = d.contentType.startsWith('image/');
            const Icon = isImage ? ImageIcon : FileText;
            const busy = busyDocId === d.id;
            return (
              <div
                key={d.id}
                className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-center shrink-0">
                  <Icon size={18} className="text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {displayLabel(d)}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {d.filename} · {formatFileSize(d.size)} ·{' '}
                    {d.uploadedAt.slice(0, 10)}
                    {d.uploadedByName ? ` · ${d.uploadedByName}` : ''}
                  </p>
                  {d.notes && (
                    <p className="text-xs text-gray-400 italic mt-0.5 truncate">
                      {d.notes}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleDownload(d)}
                    disabled={busy}
                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-[#2d5295] disabled:opacity-50"
                    title="Download"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  </button>
                  {!readOnly && (
                    <button
                      onClick={() => handleDelete(d)}
                      disabled={busy}
                      className="p-2 rounded-lg text-gray-500 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
